import * as Logger from '../../common/tools/logger';
import { PermissionAttribute, PermissionData, PermissionModel } from '../../interfaces';
import { PpsPermissionData } from '../interfaces';
import { PpsConfig } from './pps.service';
import {
  CONFIG_CORE_TOUCHPOINT_VISUALIZATION,
  CONFIG_CORE_PERM_TGP,
  CONFIG_CORE_PERM_CONSENT_STRINGS,
} from '../../config';

const moduleName = 'PermissionModelUtil';

/**
 * @public
 * @function updatePermissionModel
 * @description Function to set the permission data for a single `permissionType`.
 * @param {PermissionModel} permissionModel The model to update
 * @param {PermissionData} permissionData The permission data, which needs to be set.
 * @return {PermissionModel}
 */
export function updatePermissionModel(permissionModel: PermissionModel, permissionData: PermissionData): PermissionModel {

  // No permission model
  if (!permissionModel) {
    Logger.error(moduleName, `updatePermissionModel`, `No permission model to update.`);
    return null;
  }

  // No permission data
  if (!permissionData) {
    Logger.error(moduleName, `updatePermissionModel`, `Got no permission data to save.`);
    return null;
  }

  try {
    // Get local stored permission data.
    const localPermissionData = permissionModel.data;

    // No `localPermissionData` stored.
    if (localPermissionData.length === 0) {
      localPermissionData.push(permissionData);

      Logger.info(moduleName, `updatePermissionModel`, `Successfully set the first permission data entry for permissionType '${permissionData.usecase}'.`, permissionData);
    } else {

      // Reflects, if a permission is locally available.
      let permissionAvailable = false;

      // Loop `localPermissionData`.
      for (let i = 0; i < localPermissionData.length; i++) {

        // If the `permissionData`'s `permissionType` matches the local stored one ...
        if (localPermissionData[i].usecase.toUpperCase() === permissionData.usecase.toUpperCase()) {

          // ... overwrite it locally. (@ToDo: Maybe some comparison f.e. with timestamps here?)
          localPermissionData[i] = permissionData;

          // Update value.
          permissionAvailable = true;

          Logger.info(moduleName, `updatePermissionModel`, `Successfully updated permission model entry for permissionType '${permissionData.usecase}'.`, permissionData);
        }
      }

      // After looping, no match, new entry.
      if (!permissionAvailable) {

        // Create new entry.
        localPermissionData.push(permissionData);

        Logger.info(moduleName, `updatePermissionModel`, `Successfully created new permission model entry for permissionType '${permissionData.usecase}'.`, permissionData);
      }
    }

    return {
      identity: permissionModel.identity,
      data: localPermissionData,
      tcfVersion: permissionModel.tcfVersion,
      tcString: permissionModel.tcString,
      tcStatus: permissionModel.tcStatus
    };

  } catch (error) {
    Logger.error(moduleName, `updatePermissionData`, error);
    return {
      ...permissionModel
    };
  }
}

/**
 * Returns a clone of the given permission model
 * @param model PermissionModel to clone
 */
export function clonePermissionModel(model: PermissionModel): PermissionModel {
  return {
    identity: model.identity,
    tcString: model.tcString,
    tcfVersion: model.tcfVersion,
    tcStatus: model.tcStatus,
    data: model.data.map((perm) => {
      const permClone = {
        usecase: perm.usecase,
        status: perm.status
      };
      for (const prop in perm) {
        /* istanbul ignore else*/
        if (Object.prototype.hasOwnProperty.call(perm, prop)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (permClone as any)[prop] = (perm as any)[prop];
        }
      }
      return permClone;
    })
  };
}

/**
 * Merge a list of PermissionData objects (source) into a PermissionModel (target). If an entry with the same usecase
 * name exists in the target, its status and timestamp will be overwritten with the values from the source
 * @param target PermissionModel to be updated
 * @param source List of PermissionData
 */
export function mergePermissionModels(target: PermissionModel, source: PermissionData[]): PermissionModel {
  const newModel = clonePermissionModel(target);

  source.forEach((remoteData) => {
    const index = newModel.data.findIndex((localData) => localData.usecase.toUpperCase() === remoteData.usecase.toUpperCase());

    if (index !== -1) {
      let localTime: Date;
      let remoteTime: Date;
      if (typeof newModel.data[index].timestamp === 'string') {
        try {
          localTime = new Date(newModel.data[index].timestamp);
        } catch (ex) {
          Logger.error(moduleName, 'mergePermissionModelsByTimestamp', `invalid timestamp in local model: ${JSON.stringify(newModel.data[index], null, 2)}`);
        }
      }

      try {
        remoteTime = new Date(remoteData.timestamp);
      } catch (ex) {
        Logger.error(moduleName, 'mergePermissionModels', `invalid timestamp in remote model: ${JSON.stringify(remoteData, null, 2)}`);
      }

      if (!localTime || remoteTime > localTime) {
        // remote data is newer, overwrite local permission data
        for (const prop in remoteData) {
          /* istanbul ignore else*/
          if (Object.prototype.hasOwnProperty.call(remoteData, prop)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (newModel.data[index] as any)[prop] = (remoteData as any)[prop];
          }
        }
      }

    } else {
      // add the remote data to the local model
      newModel.data.push(remoteData);
    }
  });

  return newModel;
}

/**
 * Compares two permission models and determines if they are equivalent
 * @param modelA
 * @param modelB
 */
export function diffPermissionData(modelA: PermissionData[], modelB: PermissionData[]): boolean {
  if (modelA.length !== modelB.length) {
    return true;
  }

  function compareData(aModel: PermissionData[], bModel: PermissionData[]): boolean {
    let diff = false;

    aModel.forEach((aData) => {
      const match = bModel.find((bData) => aData.usecase.toUpperCase() === bData.usecase.toUpperCase() && aData.status.toUpperCase() === bData.status.toUpperCase());
      if (!match) {
        diff = true;
      }
    });

    return diff;
  }

  return compareData(modelA, modelB) || compareData(modelB, modelA);
}

/**
 * Compares two arrays of PermissionAttributes and determines if they are equivalent
 * @param setA
 * @param setB
 */
export function diffPermissionAttributes(setA: PermissionAttribute[], setB: PermissionAttribute[]): boolean {
  if (!(setA instanceof Array) || !(setB instanceof Array)) {
    return true;
  }

  if (setA.length !== setB.length) {
    return true;
  }

  return setA.filter((attrA) => {
    return !!setB.find((attrB) => {
      return attrB.name.toLowerCase() === attrA.name.toLowerCase() && attrB.value === attrA.value;
    })
  }).length !== setA.length;
}

/**
 * Return a list of permission with missing/falsy timestamp property values
 * @param data permissions to check
 */
export function getPermissionsWithoutTimestamps(data: PermissionData[]): PermissionData[] {
  return data.filter((permission) => !permission.timestamp);
}

/**
 * Converts a permission object from PPS to PPP format
 * @param ppsData
 */
export function ppsToPpp(ppsData: PpsPermissionData): PermissionData {
  return {
    usecase: ppsData.permission_name.toUpperCase(),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    status: (ppsData as any).status.toUpperCase(),
    timestamp: ppsData.timestamp
  }
}

/**
 * Converts a permission object from PPP to PPS format
 * @param pppData permission data in PPP format
 * @param settings additional information required by PPS
 */
export function pppToPps(pppData: PermissionData, settings: PpsConfig): PpsPermissionData {
  const permissionAttributes = pppData.permissionAttributes instanceof Array ? pppData.permissionAttributes : [];
  /* eslint-disable @typescript-eslint/camelcase */
  return {
    permission_name: pppData.usecase,
    status: pppData.status,
    origin_ip: '0.0.0.0',
    touchpoint_name: settings.touchpointName,
    touchpoint_reference: settings.touchpointReference,
    touchpoint_visualization: pppData.touchpointVisualization,
    user_agent: navigator.userAgent,
    permission_attributes: [
      ...permissionAttributes,
      {
        name: 'x-ui-app',
        value: settings.xUIApp
      }
    ]
  };
  /* eslint-enable @typescript-eslint/camelcase */
}

/**
 * Store the TRACKING_AND_PROFILING permission based on the status of the tgp permission in the current TC model
 * @param model
 */
export function syncTgp(model: PermissionModel): Promise<void> {
  if (typeof window.__tcfapi !== 'function') {
    return Promise.reject(`${moduleName} syncTgp: TCF API not available!`);
  }

  if (!model || !Array.isArray(model.data)) {
    return Promise.reject(`${moduleName} syncTgp: Invalid permission model!`);
  }

  const pmcPermission = model.data.find((perm) => {
    return perm.usecase.toUpperCase() === CONFIG_CORE_PERM_TGP;
  });
  const pmcStatus = pmcPermission ? pmcPermission.status.toUpperCase() === 'VALID' : null;

  return new Promise((resolve) => {
    window.__tcfapi('getPermission', 2, (tcfStatus) => {
      if (tcfStatus !== pmcStatus) {
        updatePermissionModel(model, {
          usecase: CONFIG_CORE_PERM_TGP,
          status: tcfStatus ? 'VALID' : 'INVALID',
          identityType: 'NETID_TPID',
          touchpointVisualization: CONFIG_CORE_TOUCHPOINT_VISUALIZATION
        });
      }
      resolve();
    }, 'tgp');
  });
}

/**
 * Store the CONSENT_STRINGS permission containing the AC and optionally TC strings from the current TC model
 * @param model
 * @param storeTcString Also add the TC string to the stored permission data
 */
export function syncConsentStrings(model: PermissionModel, storeTcString = false): Promise<void> {
  if (typeof window.__tcfapi !== 'function') {
    return Promise.reject(`${moduleName} syncConsentStrings: TCF API not available!`);
  }

  if (!model || !Array.isArray(model.data)) {
    return Promise.reject(`${moduleName} syncConsentStrings: Invalid permission model!`);
  }

  return new Promise(function(resolve) {
    let tcString: PermissionAttribute;
    let acString: PermissionAttribute;

    window.__tcfapi('getTCString', 2, (response) => {
      if (response) {
        tcString = {
          name: 'tc-string',
          value: response
        }
      }
    }, null);

    window.__tcfapi('getACString', 2, (response) => {
      if (response) {
        acString = {
          name: 'ac-string',
          value: response
        }
      }
    }, null);

    const pmcData: PermissionData = {
      usecase: CONFIG_CORE_PERM_CONSENT_STRINGS,
      status: 'VALID',
      identityType: 'NETID_TPID',
      touchpointVisualization: CONFIG_CORE_TOUCHPOINT_VISUALIZATION,
      permissionAttributes: []
    };

    if (storeTcString && tcString) {
      pmcData.permissionAttributes.push(tcString);
    }

    if (acString) {
      pmcData.permissionAttributes.push(acString);
    }

    if (pmcData.permissionAttributes.length === 0) {
      delete pmcData.permissionAttributes;
    }

    const oldPerm = model.data
      .find((entry) => entry.usecase.toUpperCase() === CONFIG_CORE_PERM_CONSENT_STRINGS);

    // make sure we only compare the ac-string and tc-string permission attributes, ignore x-ui-app etc.
    let extraAttribs: PermissionAttribute[] = [];
    if (oldPerm && oldPerm.permissionAttributes && oldPerm.permissionAttributes.length > 0 && pmcData.permissionAttributes instanceof Array) {
      extraAttribs = oldPerm.permissionAttributes
        .filter((attr) => attr.name.toLowerCase() !== 'ac-string' && attr.name.toLowerCase() !== 'tc-string');
      pmcData.permissionAttributes = pmcData.permissionAttributes.concat(extraAttribs);
    }

    let updateRequired = false;

    if (!oldPerm) {
      updateRequired = true;
    } else if (oldPerm && oldPerm.permissionAttributes instanceof Array && pmcData.permissionAttributes instanceof Array) {
      updateRequired = diffPermissionAttributes(oldPerm.permissionAttributes, pmcData.permissionAttributes);
    }

    if (updateRequired) {
      updatePermissionModel(model, pmcData);
    }

    resolve();
  });
}
